import React, { useCallback, useRef, useState } from "react";
import { Nav, THEMES } from "@bloomberg/media-ui-nav";
import type { MenuItems } from "@bloomberg/media-ui-nav/dist/types";
import { useNav } from "@foundation/nav";
import { useScrolling } from "@foundation/scroll";

import styles from "./nav.module.css";
import { Locale } from "~constants/i18n";
import { localeMap } from "~utils/i18n/localeMap";

interface NavContainerProps {
  menuItems: MenuItems;
  useGrid?: boolean;
  countryCode?: string;
  locale?: string;
}

export default function NavContainer({
  menuItems,
  countryCode,
  locale = Locale.En,
}: NavContainerProps) {
  const navRef = useRef<HTMLDivElement | null>(null);
  const [transitionCSS, setTransitionCSS] = useState({ top: "0px" });

  const onScrollUpCallback = useCallback(() => {
    setTransitionCSS({ top: "0px" });
  }, []);

  const onScrollDownCallback = useCallback(() => {
    if (navRef.current) {
      const componentHeight = navRef.current.clientHeight;

      setTransitionCSS({ top: `-${componentHeight}px` });
    }
  }, [navRef.current]);

  const trackingFunction = useCallback(
    (eventAction: string, eventLabel: string) => {
      window.dataLayer?.push({
        eventCategory: "nav",
        event: "navClick",
        eventAction,
        eventLabel,
      });
    },
    []
  );

  useScrolling({
    scrollingElementRef: navRef,
    scrollDirectionThreshold: 30,
    onScrollUpCallback,
    onScrollDownCallback,
  });

  // Nav media-ui-nav expects jp - convert 'ja' to 'jp'
  const routeLocale =
    localeMap[locale as keyof typeof localeMap]?.routeLocale || locale;

  return (
    <div className={styles.nav} style={transitionCSS} ref={navRef}>
      <Nav
        initializeSignInWithRegUI={useNav().initializeSignInWithRegUI}
        theme={THEMES.DARK}
        user={useNav().user}
        searchClient={useNav().searchClient}
        onSignOut={useNav().onSignOut}
        desktopBreakpointClassName={styles.gridNavDesktop}
        countryCode={countryCode}
        {...(menuItems ? { menuItems } : { hideMenu: true })}
        trackingFunction={trackingFunction}
        subscriptionUrl={useNav().subscriptionUrl}
        upgradeUrl={useNav().upgradeUrl}
        locale={routeLocale}
      />
    </div>
  );
}
